import React from 'react';
import { Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  VARIANT_HISTORY_PERIODS,
  VARIANT_HISTORY_PERIOD_LABELS,
  type VariantHistoryPeriod,
} from '@/shared/variantHistoryPeriods';
import { formatSnapshotDate } from './variantHistoryFormat';

interface VariantHistoryControlsProps {
  period: VariantHistoryPeriod;
  onPeriodChange: (value: string) => void;
  /** What is in the Go to date box (picked or typed). */
  dateInput: string;
  onDateChange: (value: string) => void;
  onClearDate: () => void;
  /** A complete, selectable date is applied: the period selector is disabled. */
  dateApplied: boolean;
  /** The typed date is today or later. */
  dateIsTooLate: boolean;
  /** Yesterday in PT: the latest day that can be picked. */
  latest: string;
}

/** The period selector, Go to date and Clear date, and the page note (FIG-239). */
export const VariantHistoryControls: React.FC<VariantHistoryControlsProps> = ({
  period,
  onPeriodChange,
  dateInput,
  onDateChange,
  onClearDate,
  dateApplied,
  dateIsTooLate,
  latest,
}) => (
  <>
    <div className="flex flex-wrap items-center gap-3">
      <Select value={period} onValueChange={onPeriodChange} disabled={dateApplied}>
        <SelectTrigger className="w-[180px]" aria-label="Period">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {VARIANT_HISTORY_PERIODS.map((value) => (
            <SelectItem key={value} value={value}>
              {VARIANT_HISTORY_PERIOD_LABELS[value]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <label className="inline-flex items-center gap-2 text-sm">
        Go to date
        <Input
          type="date"
          value={dateInput}
          max={latest}
          onChange={(event) => onDateChange(event.target.value)}
          aria-invalid={dateIsTooLate}
          className="w-[170px]"
        />
      </label>
      {dateInput !== '' && (
        <Button type="button" variant="ghost" size="sm" onClick={onClearDate} className="text-indigo-700">
          Clear date
        </Button>
      )}

      <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs text-muted-foreground">
        <Clock className="h-3.5 w-3.5" aria-hidden="true" />
        Data to midnight PT · recorded 6:00 AM PT
      </span>
    </div>

    {dateIsTooLate && (
      <p role="alert" className="text-sm text-red-700">
        Today and later dates can't be picked. Pick a date up to {formatSnapshotDate(latest)}.
      </p>
    )}
  </>
);
